import React, { useEffect, useRef, useState } from 'react';

/**
 * LmxDockEntry — la entrada al Studio (una vez por sesión).
 *
 *  1. La imagen de SML se forma: píxeles chicos que toman el color de cada
 *     sector, por zonas, bajo un cristal esmerilado que se aclara (~3,4 s).
 *  2. El orbe sube desde abajo y se apoya DEBAJO del título (no lo tapa),
 *     flotando, con el aviso "Tocá la burbuja para empezar".
 *  3. Al tocarlo, el orbe vuela a su cuna en el dock y el telón se disuelve.
 *
 * Clic o tecla durante el paso 1 lo saltea. prefers-reduced-motion: sin
 * animaciones, aparece directo el paso 2.
 */
const INTRO_MS = 3400;

export const LmxDockEntry = ({ image, orb, onDone }) => {
  const canvasRef = useRef(null);
  const orbRef = useRef(null);
  const [phase, setPhase] = useState('forming'); // forming → orb → leaving
  const reduce = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // ── 1 · la imagen se forma ──
  useEffect(() => {
    if (reduce) { setPhase('orb'); return undefined; }
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const W = cv.width = window.innerWidth;
    const H = cv.height = window.innerHeight;
    const mk = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; };
    const full = mk(W, H); const fctx = full.getContext('2d');
    const cell = Math.max(4, Math.round(Math.min(W, H) / 170));
    const gw = Math.ceil(W / cell); const gh = Math.ceil(H / cell);
    const mc = mk(gw, gh); const mctx = mc.getContext('2d'); const md = mctx.createImageData(gw, gh);
    const P = mk(W, H); const pctx = P.getContext('2d'); pctx.imageSmoothingEnabled = false;
    const grid = mk(W, H); const gctx = grid.getContext('2d');
    gctx.fillStyle = 'rgba(5,6,10,.55)';
    for (let x = 0; x < W; x += cell) gctx.fillRect(x, 0, 1, H);
    for (let y = 0; y < H; y += cell) gctx.fillRect(0, y, W, 1);
    const glass = mk(W / 4, H / 4); const glctx = glass.getContext('2d');
    const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - ((-2 * k + 2) ** 3) / 2);
    const sm = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
    let col = null; let rt = null; let raf = 0; let t0 = 0; let done = false;

    const finish = () => {
      if (done) return; done = true; cancelAnimationFrame(raf);
      ctx.globalAlpha = 1; ctx.drawImage(full, 0, 0);
      setPhase('orb');
    };
    const prep = () => {
      const s = mk(gw, gh); const sc = s.getContext('2d');
      sc.imageSmoothingQuality = 'high'; sc.drawImage(full, 0, 0, gw, gh);
      col = sc.getImageData(0, 0, gw, gh).data; rt = new Float32Array(gw * gh);
      const cs = 14; const nw = Math.ceil(gw / cs) + 2; const nh = Math.ceil(gh / cs) + 2;
      const nv = new Float32Array(nw * nh).map(() => Math.random());
      for (let y = 0; y < gh; y += 1) {
        for (let x = 0; x < gw; x += 1) {
          const fx = x / cs; const fy = y / cs; const ix = fx | 0; const iy = fy | 0;
          const tx = fx - ix; const ty = fy - iy; const u = tx * tx * (3 - 2 * tx); const v = ty * ty * (3 - 2 * ty);
          const a = nv[iy * nw + ix]; const b = nv[iy * nw + ix + 1];
          const c = nv[(iy + 1) * nw + ix]; const d = nv[(iy + 1) * nw + ix + 1];
          const n = a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
          const i = (y * gw + x) * 4; const lum = (col[i] * 0.3 + col[i + 1] * 0.59 + col[i + 2] * 0.11) / 255;
          rt[y * gw + x] = Math.min(0.62, Math.max(0, 0.04 + 0.5 * (0.7 * n + 0.3 * Math.random()) - 0.16 * lum));
        }
      }
    };
    const frame = (now) => {
      if (done) return; if (!t0) t0 = now;
      const k = Math.min(1, (now - t0) / INTRO_MS);
      const d = md.data;
      for (let j = 0, n = gw * gh; j < n; j += 1) {
        const a = sm(rt[j], rt[j] + 0.2, k); const i = j * 4; const f = 1 + 0.35 * (1 - a) * (a > 0 ? 1 : 0);
        d[i] = Math.min(255, col[i] * f); d[i + 1] = Math.min(255, col[i + 1] * f); d[i + 2] = Math.min(255, col[i + 2] * f); d[i + 3] = a * 255;
      }
      mctx.putImageData(md, 0, 0);
      pctx.clearRect(0, 0, W, H); pctx.drawImage(mc, 0, 0, gw * cell, gh * cell);
      pctx.globalAlpha = 1 - sm(0.55, 0.95, k); pctx.drawImage(grid, 0, 0); pctx.globalAlpha = 1;
      ctx.globalAlpha = 1; ctx.fillStyle = '#05060a'; ctx.fillRect(0, 0, W, H);
      const g = 1 + 22 * ((1 - ease(Math.min(1, k / 0.8))) ** 1.6);
      if (g > 1.25) {
        const w2 = Math.max(2, Math.round(W / g)); const h2 = Math.max(2, Math.round(H / g));
        if (glass.width !== w2) { glass.width = w2; glass.height = h2; }
        glctx.imageSmoothingQuality = 'high'; glctx.clearRect(0, 0, w2, h2); glctx.drawImage(P, 0, 0, w2, h2);
        ctx.imageSmoothingEnabled = true; ctx.drawImage(glass, 0, 0, W, H);
        ctx.globalAlpha = sm(0.25, 0.8, k) * 0.85; ctx.imageSmoothingEnabled = false; ctx.drawImage(P, 0, 0); ctx.globalAlpha = 1;
      } else { ctx.imageSmoothingEnabled = false; ctx.drawImage(P, 0, 0); }
      const m = sm(0.72, 1, k);
      if (m > 0) { ctx.globalAlpha = m; ctx.imageSmoothingEnabled = true; ctx.drawImage(full, 0, 0); ctx.globalAlpha = 1; }
      const sx = -W * 0.4 + W * 1.8 * sm(0.2, 0.95, k);
      const gr = ctx.createLinearGradient(sx - W * 0.25, 0, sx + W * 0.25, H * 0.6);
      gr.addColorStop(0, 'rgba(255,255,255,0)'); gr.addColorStop(0.5, `rgba(235,242,255,${0.07 * (1 - m)})`); gr.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = gr; ctx.fillRect(0, 0, W, H);
      if (k >= 1) { finish(); return; }
      raf = requestAnimationFrame(frame);
    };
    const img = new Image();
    img.onload = () => {
      const s = Math.max(W / img.width, H / img.height); const w = img.width * s; const h = img.height * s;
      fctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h); prep(); raf = requestAnimationFrame(frame);
    };
    img.onerror = finish;
    img.src = image;
    const skip = () => finish();
    window.addEventListener('keydown', skip, { once: true });
    cv.addEventListener('click', skip);
    return () => { done = true; cancelAnimationFrame(raf); window.removeEventListener('keydown', skip); };
  }, [image, reduce]);

  // ── 3 · el orbe vuela a su cuna ──
  const leave = () => {
    if (phase !== 'orb') return;
    const from = orbRef.current?.getBoundingClientRect();
    const target = document.querySelector('[data-testid="lmx-dock-orb"]')?.getBoundingClientRect();
    setPhase('leaving');
    if (!from || !target || reduce) { onDone(); return; }
    const dx = (target.left + target.width / 2) - (from.left + from.width / 2);
    const dy = (target.top + target.height / 2) - (from.top + from.height / 2);
    const s = target.width / from.width;
    orbRef.current.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`;
    setTimeout(onDone, 820);
  };

  return (
    <div className={`lmx-entry is-${phase}`} data-testid="lmx-dock-curtain">
      <div className="lmx-entry-bg" style={{ backgroundImage: `url("${image}")` }} />
      {!reduce && <canvas className="lmx-entry-canvas" ref={canvasRef} aria-label="Intro de Studio Media Labs" />}
      <div className="lmx-entry-orb-wrap">
        <button
          type="button"
          ref={orbRef}
          className="lmx-entry-orb"
          onClick={leave}
          aria-label="Abrir LMX"
          data-testid="lmx-entry-orb"
          tabIndex={phase === 'orb' ? 0 : -1}
        >
          <span className="lmx-entry-glow" aria-hidden />
          <img src={orb} alt="" />
        </button>
        <p className="lmx-entry-hint">Tocá la burbuja para empezar</p>
      </div>
      {phase === 'forming' && <span className="lmx-entry-skip">Clic para saltear</span>}
    </div>
  );
};

export default LmxDockEntry;
